import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useExchangeRateHistory } from "@/hooks/useExchangeRateQueries";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Calendar } from "lucide-react";
import {
  ExchangeRateHistoryDto,
  ExchangeRateChartData,
} from "@/types/exchangeRate";
import { formatDateTime } from "@/lib/utils";
import { getCurrencyFlag } from "@/lib/currencyUtils";
import {
  CHART_GRID_STROKE,
  CHART_TICK_FILL,
  CHART_GRID_STROKE_OPACITY,
  chartTooltipContentStyle,
} from "@/lib/chartTheme";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { subDays, subYears, format } from "date-fns";

/**
 * Date range filter options
 */
type DateRangeFilter = "last30days" | "last90days" | "last1year" | "all";

/**
 * Exchange Rate History Page
 *
 * Displays historical exchange rates for a specific currency.
 *
 * Features:
 * - Historical rates table with all metadata
 * - Line chart showing rate changes over time
 * - Date range filters (30 days, 90 days, 1 year, all)
 * - Shows who updated rates and when
 * - Source and notes for each change
 * - "Current" badge for active rate
 */
export function ExchangeRateHistoryPage() {
  const { currency } = useParams<{ currency: string }>();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRangeFilter>("last90days");
  const [page, setPage] = useState(1);

  // Calculate date range
  const getDateRange = (): { fromDate?: string; toDate?: string } => {
    const now = new Date();
    switch (dateRange) {
      case "last30days":
        return { fromDate: subDays(now, 30).toISOString() };
      case "last90days":
        return { fromDate: subDays(now, 90).toISOString() };
      case "last1year":
        return { fromDate: subYears(now, 1).toISOString() };
      case "all":
      default:
        return {};
    }
  };

  const { data, isLoading, error, refetch } = useExchangeRateHistory({
    currency: currency || "",
    ...getDateRange(),
    page,
    pageSize: 20,
  });

  // Prepare chart data
  const chartData: ExchangeRateChartData[] =
    data?.data.map((rate) => ({
      date: format(new Date(rate.effectiveFrom), "MMM dd, yyyy"),
      rate: rate.rateToVND,
      label: rate.displayRate,
    })) || [];

  if (!currency) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Currency not specified</p>
        <Button
          onClick={() => navigate("/admin/settings/exchange-rates")}
          className="mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Exchange Rates
        </Button>
      </div>
    );
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${currency} Exchange Rate History`}
        subtitle="Historical exchange rate changes and trends"
        back={{
          label: "Back to Exchange Rates",
          onClick: () => navigate("/admin/settings/exchange-rates"),
        }}
        leading={<span className="text-4xl">{getCurrencyFlag(currency)}</span>}
      />

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Date Range Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={dateRange}
            onValueChange={(value) => {
              setDateRange(value as DateRangeFilter);
              setPage(1); // Reset to page 1 when filter changes
            }}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last30days">Last 30 Days</SelectItem>
              <SelectItem value="last90days">Last 90 Days</SelectItem>
              <SelectItem value="last1year">Last 1 Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Rate Trend Chart */}
      {!isLoading && chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Rate Trend</CardTitle>
            <CardDescription>
              Exchange rate changes over time - 1 {currency} = X VND
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.slice().reverse()}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: CHART_TICK_FILL, fontSize: 11 }}
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: CHART_TICK_FILL, fontSize: 11 }}
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip
                  contentStyle={chartTooltipContentStyle}
                  formatter={(value: number) => [
                    value.toLocaleString(),
                    "Rate",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="var(--brand)"
                  strokeWidth={2}
                  dot={{ fill: "var(--brand)" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Rate History
            {data?.pagination?.totalItems !== undefined && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({data.pagination?.totalItems} total)
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Complete history of all exchange rate changes with audit information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : data && data.data.length > 0 ? (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rate</TableHead>
                      <TableHead>Effective From</TableHead>
                      <TableHead>Effective To</TableHead>
                      <TableHead className="w-[80px]">Version</TableHead>
                      <TableHead>Updated By</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map((rate: ExchangeRateHistoryDto) => (
                      <TableRow key={`${rate.currency}-v${rate.version}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">
                              {rate.displayRate}
                            </span>
                            {rate.isCurrent && (
                              <Badge variant="default" className="ml-2">
                                Current
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {formatDateTime(rate.effectiveFrom)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {rate.effectiveTo
                              ? formatDateTime(rate.effectiveTo)
                              : "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-mono">
                            v{rate.version}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {rate.updatedBy || (
                              <span className="text-muted-foreground">
                                System
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{rate.source}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px] truncate text-sm text-muted-foreground">
                            {rate.notes || "-"}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.pagination.currentPage} of{" "}
                    {data.pagination.totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={!data.pagination.hasPrev}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!data.pagination.hasNext}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground">
                No history found for this date range
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Try selecting a different date range
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
